import { isAxiosError } from 'axios';
import {
  FINPSP_DEPOSIT_TYPE,
  FINPSP_DEPOSIT_TYPE_ALIASES,
  FINPSP_PAYMENT_STATE,
  FINPSP_PAYMENT_STATUS,
  type FinpspDepositType,
} from '../../constants/finpsp';
import { LEDGER_STATUS } from '../../types/enums';
import type { FinpspPayment } from '../../types/finpsp';

const FINPSP_CHANNEL_ALIASES: Record<string, FinpspDepositType> = {
  ...FINPSP_DEPOSIT_TYPE,
  ...FINPSP_DEPOSIT_TYPE_ALIASES,
};

export const getFinpspDepositType = (payment: Pick<FinpspPayment, 'type'>) =>
  FINPSP_CHANNEL_ALIASES[payment.type ?? ''] ?? FINPSP_DEPOSIT_TYPE.P2A;

export const isFinpspTerminal = (payment?: Pick<FinpspPayment, 'status'>) =>
  payment?.status === FINPSP_PAYMENT_STATUS.APPROVED ||
  payment?.status === FINPSP_PAYMENT_STATUS.REJECTED ||
  payment?.status === FINPSP_PAYMENT_STATUS.CANCELLED;

const FINPSP_STATUS_LABEL_KEYS: Record<FinpspPayment['status'], string> = {
  [FINPSP_PAYMENT_STATUS.REQUESTED]: LEDGER_STATUS.PENDING,
  [FINPSP_PAYMENT_STATUS.RESERVED]: LEDGER_STATUS.PENDING,
  [FINPSP_PAYMENT_STATUS.PENDING]: LEDGER_STATUS.PENDING,
  [FINPSP_PAYMENT_STATUS.APPROVED]: LEDGER_STATUS.COMPLETED,
  [FINPSP_PAYMENT_STATUS.REJECTED]: LEDGER_STATUS.REJECTED,
  [FINPSP_PAYMENT_STATUS.CANCELLED]: LEDGER_STATUS.CANCELED,
};

export const getFinpspStatusKey = (payment: Pick<FinpspPayment, 'status'>) => FINPSP_STATUS_LABEL_KEYS[payment.status];

const FINPSP_INSTRUCTION_STATUS_KEYS: Partial<Record<FinpspPayment['state'], string>> = {
  [FINPSP_PAYMENT_STATE.REVIEW]: 'finpspReview',
};

export const getFinpspInstructionStatusKey = (payment: FinpspPayment) => {
  if (isFinpspTerminal(payment)) {
    return getFinpspStatusKey(payment);
  }

  return (
    FINPSP_INSTRUCTION_STATUS_KEYS[payment.state] ?? (payment.sim_number ? 'finpspWaiting' : 'finpspAssigningNumber')
  );
};

// A transport failure or malformed acknowledgement cannot prove that no payment was created.
export const isFinpspSubmissionUncertain = (error: unknown) => {
  if (!isAxiosError(error) || !error.response) {
    return true;
  }

  const status = error.response.status;

  return status >= 500 || status === 408;
};

export const getFinpspCheckoutUrl = (value?: string | null) => {
  try {
    const url = new URL(value ?? '');

    if (url.protocol === 'https:' && !url.username && !url.password) {
      return url.href;
    }
  } catch {
    // Missing or invalid checkout data leaves the created payment unresolved.
  }

  return null;
};
